// ── Cauldron DEX indexer client ──────────────────────────────────
//
// Read-only access to the public Riften Labs Cauldron indexer, which tracks
// every CashToken liquidity pool on Bitcoin Cash. We use it to turn a $TICKER
// mentioned in a note into a rich token card: name, icon, live price, 24h
// change, TVL, APY.
//
// SECURITY: this module performs HTTP GETs only. It never signs, never touches
// the user's keys or wallet, and never writes on-chain. Requests are routed
// through the app's CORS proxy (same pattern as useBCHPrice). Token icons are
// served from Riften's own metadata host as plain https URLs, so there is no
// ipfs:// scheme to sanitize.

const INDEXER = 'https://indexer.riften.net';
const META_ICON = 'https://meta.riften.net/icon'; // /<token_id> → png
const CORS_PROXY = 'https://proxy.shakespeare.diy/?url=';

/** Official Cauldron swap page for a token. */
export function cauldronSwapUrl(tokenId: string): string {
  return `https://app.cauldron.quest/swap/${tokenId}`;
}

/** Riften metadata icon URL for a token (plain https, no ipfs gateway needed). */
export function tokenIconUrl(tokenId: string): string {
  return `${META_ICON}/${tokenId}`;
}

function proxied(url: string): string {
  return CORS_PROXY + encodeURIComponent(url);
}

// ── Public token shape (normalised from the indexer's raw record) ──

export interface CauldronToken {
  tokenId: string;
  name: string;
  symbol: string;
  decimals: number;
  iconUrl: string;
  /** BCH per 1 token. */
  priceBch: number;
  /** USD per 1 token. */
  priceUsd: number;
  /** 24h price change, percent (can be negative). */
  change24h: number;
  /** Total value locked, in BCH (BCH side of all pools). */
  tvlBch: number;
  /** 30d APY, percent. */
  apy: number;
  /** 24h trade volume in sats (0 if none). */
  volumeSats: number;
  /** Normalised 7d price series for a sparkline (may be empty). */
  series7d: number[];
}

// The raw indexer record carries far more than we need; type only what we read.
interface RawToken {
  token_id: string;
  display_name?: string;
  display_symbol?: string;
  bcmr?: {
    name?: string;
    token?: { symbol?: string; decimals?: number };
  };
  price_now?: number;
  price_now_usd?: number;
  change_24h_bp?: number;
  tvl_sats?: number;
  apy_30d_bp?: number;
  trade_volume?: number;
  price_series_7d?: number[];
}

// `price_now` is scaled by 1e12 in the indexer (BCH-per-token * 1e12).
const PRICE_SCALE = 1e12;
const SATS_PER_BCH = 1e8;

function normalise(raw: RawToken): CauldronToken | null {
  if (!raw || typeof raw.token_id !== 'string') return null;
  const name = raw.bcmr?.name || raw.display_name || '';
  const symbol = raw.bcmr?.token?.symbol || raw.display_symbol || '';
  return {
    tokenId: raw.token_id,
    name: name || symbol || raw.token_id.slice(0, 8),
    symbol: symbol || raw.token_id.slice(0, 6),
    decimals: typeof raw.bcmr?.token?.decimals === 'number' ? raw.bcmr.token.decimals : 0,
    iconUrl: tokenIconUrl(raw.token_id),
    priceBch: typeof raw.price_now === 'number' ? raw.price_now / PRICE_SCALE : 0,
    priceUsd: typeof raw.price_now_usd === 'number' ? raw.price_now_usd : 0,
    change24h: typeof raw.change_24h_bp === 'number' ? raw.change_24h_bp / 100 : 0,
    tvlBch: typeof raw.tvl_sats === 'number' ? raw.tvl_sats / SATS_PER_BCH : 0,
    apy: typeof raw.apy_30d_bp === 'number' ? raw.apy_30d_bp / 100 : 0,
    volumeSats: typeof raw.trade_volume === 'number' ? raw.trade_volume : 0,
    series7d: Array.isArray(raw.price_series_7d) ? raw.price_series_7d.filter(n => typeof n === 'number') : [],
  };
}

// ── In-memory + localStorage cache ───────────────────────────────
// Token data changes slowly enough that a short TTL keeps the feed snappy
// without hammering the indexer when many cards render at once.

const CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes
const NEG_TTL_MS = 10 * 60 * 1000;  // remember "no such ticker" for 10 min
const LS_PREFIX = 'bchnostr:cauldron:';

interface CacheEntry { token: CauldronToken | null; at: number; }

const _memCache = new Map<string, CacheEntry>();

function cacheKey(kind: 'sym' | 'id', value: string): string {
  return `${kind}:${value.toLowerCase()}`;
}

function readCache(key: string): CacheEntry | null {
  const mem = _memCache.get(key);
  if (mem) return mem;
  try {
    const raw = localStorage.getItem(LS_PREFIX + key);
    if (raw) {
      const parsed = JSON.parse(raw) as CacheEntry;
      _memCache.set(key, parsed);
      return parsed;
    }
  } catch { /* ignore */ }
  return null;
}

function writeCache(key: string, entry: CacheEntry): void {
  _memCache.set(key, entry);
  try { localStorage.setItem(LS_PREFIX + key, JSON.stringify(entry)); } catch { /* ignore */ }
}

function isFresh(entry: CacheEntry): boolean {
  const ttl = entry.token ? CACHE_TTL_MS : NEG_TTL_MS;
  return Date.now() - entry.at < ttl;
}

// De-dupe concurrent lookups for the same key (feed renders many cards at once).
const _inflight = new Map<string, Promise<CauldronToken | null>>();

async function fetchJson(url: string): Promise<unknown> {
  const res = await fetch(proxied(url), { signal: AbortSignal.timeout(10000) });
  if (!res.ok) throw new Error(`indexer ${res.status}`);
  return res.json();
}

/**
 * Resolve a token by its ticker symbol (case-insensitive). Returns null when
 * no CashToken with that symbol has an active Cauldron pool — callers should
 * fall back to rendering the raw `$TICKER` text in that case.
 */
export async function resolveTokenBySymbol(symbol: string): Promise<CauldronToken | null> {
  const sym = symbol.trim();
  if (!sym) return null;
  const key = cacheKey('sym', sym);

  const cached = readCache(key);
  if (cached && isFresh(cached)) return cached.token;

  const existing = _inflight.get(key);
  if (existing) return existing;

  const task = (async (): Promise<CauldronToken | null> => {
    try {
      const url = `${INDEXER}/cauldron/tokens/search_cached?q=${encodeURIComponent(sym)}&limit=10&by=tvl&order=desc`;
      const data = await fetchJson(url);
      const list = Array.isArray(data) ? (data as RawToken[]) : [];
      // Prefer an exact (case-insensitive) symbol match; else the highest-TVL hit.
      const exact = list.find(t =>
        (t.bcmr?.token?.symbol || t.display_symbol || '').toLowerCase() === sym.toLowerCase());
      const chosen = exact || list[0] || null;
      const token = chosen ? normalise(chosen) : null;
      writeCache(key, { token, at: Date.now() });
      // Also warm the id-cache so a later token-page load is instant.
      if (token) writeCache(cacheKey('id', token.tokenId), { token, at: Date.now() });
      return token;
    } catch {
      // On failure, serve stale data if we have any rather than a hard miss.
      return cached ? cached.token : null;
    } finally {
      _inflight.delete(key);
    }
  })();

  _inflight.set(key, task);
  return task;
}

/** Resolve a token by its 64-char hex token id. */
export async function resolveTokenById(tokenId: string): Promise<CauldronToken | null> {
  const id = tokenId.trim().toLowerCase();
  if (!/^[0-9a-f]{64}$/.test(id)) return null;
  const key = cacheKey('id', id);

  const cached = readCache(key);
  if (cached && isFresh(cached)) return cached.token;

  const existing = _inflight.get(key);
  if (existing) return existing;

  const task = (async (): Promise<CauldronToken | null> => {
    try {
      const url = `${INDEXER}/cauldron/tokens/list_cached_by_ids?ids=${id}`;
      const data = await fetchJson(url);
      const list = Array.isArray(data) ? (data as RawToken[]) : [];
      const token = list[0] ? normalise(list[0]) : null;
      writeCache(key, { token, at: Date.now() });
      return token;
    } catch {
      return cached ? cached.token : null;
    } finally {
      _inflight.delete(key);
    }
  })();

  _inflight.set(key, task);
  return task;
}

// ── Token list / discovery ───────────────────────────────────────

export type TokenSortField = 'tvl' | 'volume' | 'name' | 'symbol' | 'score';

const LIST_TTL_MS = 60 * 1000; // 1 minute — the discovery page can be a touch stale
const _listCache = new Map<string, { tokens: CauldronToken[]; at: number }>();

/**
 * List tokens that have active Cauldron pools, sorted by the given field.
 * Warms the per-id cache so clicking into a token page is instant. Serves a
 * short-lived cache and falls back to stale data on network failure.
 */
export async function fetchTokenList(
  by: TokenSortField = 'tvl',
  limit = 100,
): Promise<CauldronToken[]> {
  const key = `${by}:${limit}`;
  const cached = _listCache.get(key);
  if (cached && Date.now() - cached.at < LIST_TTL_MS) return cached.tokens;

  try {
    const url = `${INDEXER}/cauldron/tokens/list_cached?limit=${limit}&by=${by}&order=desc`;
    const data = await fetchJson(url);
    const list = Array.isArray(data) ? (data as RawToken[]) : [];
    const tokens = list
      .map(normalise)
      .filter((t): t is CauldronToken => t !== null);
    _listCache.set(key, { tokens, at: Date.now() });
    // Warm the id-cache so token pages opened from here load instantly.
    for (const t of tokens) {
      writeCache(cacheKey('id', t.tokenId), { token: t, at: Date.now() });
    }
    return tokens;
  } catch {
    return cached ? cached.tokens : [];
  }
}

// ── Pools (for the token page liquidity tab) ─────────────────────

export interface CauldronPool {
  poolId: string;
  ownerAddr: string;
  ownerPkh: string;
  sats: number;
  tokens: number;
  txid: string;
}

interface RawPool {
  pool_id?: string;
  owner_p2pkh_addr?: string;
  owner_pkh?: string;
  sats?: number;
  tokens?: number;
  txid?: string;
}

/** List active liquidity pools for a token id, largest first. */
export async function fetchPoolsForToken(tokenId: string): Promise<CauldronPool[]> {
  const id = tokenId.trim().toLowerCase();
  if (!/^[0-9a-f]{64}$/.test(id)) return [];
  try {
    const url = `${INDEXER}/cauldron/pool/active?token=${id}`;
    const data = await fetchJson(url);
    const active = (data as { active?: RawPool[] })?.active;
    if (!Array.isArray(active)) return [];
    return active
      .map((p): CauldronPool => ({
        poolId: String(p.pool_id || ''),
        ownerAddr: String(p.owner_p2pkh_addr || ''),
        ownerPkh: String(p.owner_pkh || ''),
        sats: typeof p.sats === 'number' ? p.sats : 0,
        tokens: typeof p.tokens === 'number' ? p.tokens : 0,
        txid: String(p.txid || ''),
      }))
      .sort((a, b) => b.sats - a.sats);
  } catch {
    return [];
  }
}

// ── Swap quote (read-only estimate) ──────────────────────────────
//
// Cauldron pools are constant-product (x·y=k) AMMs. We estimate how many
// tokens a given BCH (sats) input buys by routing the whole input into the
// single DEEPEST pool and applying the standard 0.3% pool fee.
//
// This is deliberately an ESTIMATE, not a quote the app will execute:
//   - Real Cauldron pools carry per-pool `poolFeeRate` + a `platformFeeRate`
//     we don't model here; we assume a flat 0.3%.
//   - A large order would be split across several pools for a better rate;
//     we use only the deepest pool, so our estimate is CONSERVATIVE for big
//     orders (the user gets at least this much on Cauldron, usually more).
//   - The final executed amount is whatever Cauldron computes at swap time.
// The UI must label it an estimate and hand execution off to Cauldron.

const POOL_FEE_BPS = 30; // 0.3% standard AMM pool fee (basis points)

export interface SwapQuote {
  /** BCH input, in satoshis. */
  inputSats: number;
  /** Estimated token output, in the token's BASE units. */
  outTokenBase: number;
  /** Price impact vs. the pool's spot price, as a fraction (0.02 = 2%). */
  priceImpact: number;
  /** The pool used for the estimate. */
  poolId: string;
  /** True when no pool had enough liquidity / none existed. */
  insufficient: boolean;
}

/**
 * Constant-product output for selling `amountIn` of reserveIn into a pool,
 * net of a bps fee. All values are plain numbers (base units / sats).
 * Returns 0 if inputs are non-positive.
 */
function cpOut(amountIn: number, reserveIn: number, reserveOut: number, feeBps: number): number {
  if (amountIn <= 0 || reserveIn <= 0 || reserveOut <= 0) return 0;
  const amountInWithFee = amountIn * (10000 - feeBps);
  const num = amountInWithFee * reserveOut;
  const den = reserveIn * 10000 + amountInWithFee;
  return num / den;
}

/**
 * Estimate buying a token with BCH across the given pools. `inputSats` is the
 * BCH spend in satoshis. Uses the deepest pool (by sats reserve). Pure maths,
 * no network — pass in pools from fetchPoolsForToken().
 */
export function estimateBchToToken(
  inputSats: number,
  pools: CauldronPool[],
): SwapQuote {
  const empty: SwapQuote = {
    inputSats, outTokenBase: 0, priceImpact: 0, poolId: '', insufficient: true,
  };
  if (!Array.isArray(pools) || pools.length === 0 || inputSats <= 0) return empty;

  // Deepest pool by BCH reserve.
  const pool = pools.reduce((a, b) => (b.sats > a.sats ? b : a));
  if (pool.sats <= 0 || pool.tokens <= 0) return empty;

  const out = cpOut(inputSats, pool.sats, pool.tokens, POOL_FEE_BPS);
  if (out <= 0) return empty;
  // Can't drain more than the pool holds.
  const outTokenBase = Math.min(out, pool.tokens - 1);

  // Spot price (sats per token base unit) before the trade, vs. effective.
  const spotSatsPerTokenBase = pool.sats / pool.tokens;
  const effSatsPerTokenBase = inputSats / outTokenBase;
  const priceImpact = spotSatsPerTokenBase > 0
    ? Math.max(0, (effSatsPerTokenBase - spotSatsPerTokenBase) / spotSatsPerTokenBase)
    : 0;

  return {
    inputSats,
    outTokenBase,
    priceImpact,
    poolId: pool.poolId,
    insufficient: false,
  };
}

/**
 * Convenience: estimate how many whole tokens a USD amount buys, using the
 * live BCH price to convert USD→sats first. Returns the whole-token estimate
 * and the sats spent. `bchPriceUsd` is USD per 1 BCH.
 */
export function estimateUsdToToken(
  usd: number,
  bchPriceUsd: number,
  pools: CauldronPool[],
  tokenDecimals: number,
): { outWhole: number; inputSats: number; priceImpact: number; insufficient: boolean } {
  if (usd <= 0 || bchPriceUsd <= 0) {
    return { outWhole: 0, inputSats: 0, priceImpact: 0, insufficient: true };
  }
  const inputSats = Math.round((usd / bchPriceUsd) * SATS_PER_BCH);
  const q = estimateBchToToken(inputSats, pools);
  const factor = 10 ** Math.max(0, Math.floor(tokenDecimals));
  return {
    outWhole: q.outTokenBase / factor,
    inputSats,
    priceImpact: q.priceImpact,
    insufficient: q.insufficient,
  };
}

// ── Formatting helpers (shared by card + page) ───────────────────

export function formatUsd(v: number): string {
  if (!isFinite(v) || v <= 0) return '$0';
  if (v >= 1e9) return '$' + (v / 1e9).toFixed(2) + 'B';
  if (v >= 1e6) return '$' + (v / 1e6).toFixed(2) + 'M';
  if (v >= 1e3) return '$' + (v / 1e3).toFixed(1) + 'K';
  if (v < 0.01) return '$' + v.toExponential(2);
  return '$' + v.toFixed(v < 1 ? 4 : 2);
}

export function formatBch(v: number): string {
  if (!isFinite(v) || v <= 0) return '0 BCH';
  if (v < 0.0001) return v.toExponential(2) + ' BCH';
  return v.toFixed(v < 1 ? 4 : 2) + ' BCH';
}

export function formatPricePerToken(priceUsd: number): string {
  if (!isFinite(priceUsd) || priceUsd <= 0) return '—';
  if (priceUsd < 0.000001) return '$' + priceUsd.toExponential(2);
  if (priceUsd < 1) return '$' + priceUsd.toFixed(6);
  return '$' + priceUsd.toFixed(4);
}
